import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { useOnReconnect } from '../hooks/useOnReconnect';
import PinGolfBottomNav from '../components/PinGolfBottomNav';
import PinGolfStandingsTable from '../components/PinGolfStandingsTable';
import PinGolfStats from '../components/PinGolfStats';
import PinGolfMyWeeks from '../components/PinGolfMyWeeks';
import { pickCurrentWeek, formatHandicap as formatHandicapText } from '../utils/pingolfScoring';
import { EVENTS_PATH } from '../utils/pingolfNav';
import { DSAlert, DSBadge, DSButton, DSCard, DSIcon } from '../design-system/primitives';
import pingolfHero from '../assets/pingolf-hero.webp';
import './pingolfHeroShared.css';
import './pingolfShared.css';
import './PinGolfLeagueOverview.css';
import { personName } from '../utils/personName';

const STATUS_CONFIG = {
  active: { label: 'Season Running', variant: 'success' },
  completed: { label: 'Season Complete', variant: 'info' }
};

// A night that is already scored, canceled, or part of a finalized week is over as far as a
// player is concerned, whatever its calendar date says. Dates alone are not enough: a host can
// finalize early, and league nights are scheduled well ahead - so without this the card
// happily pointed at a settled night and skipped the one you can actually still play.
function isSessionStillPlayable(session, week, pingolfEvents) {
  if (week.finalized_at) return false;

  const event = pingolfEvents.find(candidate => String(candidate.id) === String(session.event_id));
  const lifecycle = event?.event_metadata?.pingolf || {};
  return !lifecycle.finalized_at && !lifecycle.canceled_at;
}

// A league night stays "next" well past its start time: doors open at 6 but people keep
// buying cards and playing into the small hours, and dropping the card the moment the clock
// ticks past the start left players with no way into the night they were standing in. Keep it
// until the calendar day it started on is over; a host who finalizes or cancels sooner still
// clears it immediately via isSessionStillPlayable.
function sessionVisibleUntilMs(eventDate) {
  const start = new Date(eventDate);
  if (Number.isNaN(start.getTime())) return null;
  const endOfDay = new Date(start);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime();
}

function pickNextEvent(weeks, pingolfEvents) {
  const nowMs = Date.now();
  const upcoming = weeks
    // The week number lives on the week, not the session, and the card's title needs it -
    // carry it down rather than parsing it back out of the event title.
    .flatMap(week => (week.sessions || [])
      .filter(session => isSessionStillPlayable(session, week, pingolfEvents))
      .map(session => ({ ...session, week_number: week.week_number })))
    .filter(session => {
      const visibleUntil = session.event_date ? sessionVisibleUntilMs(session.event_date) : null;
      return visibleUntil !== null && visibleUntil > nowMs;
    })
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  return upcoming[0] || null;
}

// A league night's event title reads "<Season> - Week N", and this card sits directly under a
// hero already saying the season name. Drop the redundant half so the title fits one line;
// keep the session label when a week runs more than one host night, since "Week 1" twice over
// would be ambiguous.
function nextEventTitle(nextEvent) {
  if (nextEvent.week_number === null || nextEvent.week_number === undefined) {
    return nextEvent.event_title || 'Upcoming session';
  }
  return [`Week ${nextEvent.week_number}`, nextEvent.session_label].filter(Boolean).join(' - ');
}

const formatEventDate = (eventDate) => new Date(eventDate).toLocaleDateString('en-US', {
  weekday: 'short',
  month: 'numeric',
  day: 'numeric'
});

const formatEventTime = (eventDate) => new Date(eventDate).toLocaleTimeString('en-US', {
  hour: 'numeric',
  minute: '2-digit'
});

const formatShortDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const formatCurrency = (amount) => `$${Math.round(amount).toLocaleString('en-US')}`;

// Split amounts land on cents ($8 * 45% = $3.60), so the breakdown needs them where the
// headline pool total doesn't.
const formatMoney = (amount) => {
  const value = Number(amount) || 0;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2
  })}`;
};

const PRIZE_SPLIT_ROWS = [
  { key: 'champion', label: 'Champion + trophy' },
  { key: 'runner_up', label: 'Runner-up' },
  { key: 'third', label: 'Third' },
  { key: 'fourth', label: 'Fourth' }
];

function getSeasonDateRange(season) {
  if (!season?.start_date) return null;
  const start = new Date(season.start_date);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + (Math.max(season.week_count, 1) - 1) * 7 * 24 * 60 * 60 * 1000);
  return `${formatShortDate(start)} - ${formatShortDate(end)}`;
}

const hostDisplayName = (host) => (
  personName(host) || null
);

// The week's sessions carry the event id but not who is running it, so the host comes off the
// PinGolf event summary the page already fetches for card counts. A night can have more than
// one host assigned; list them all rather than silently picking one.
function getEventHostNames(eventId, pingolfEvents) {
  const match = pingolfEvents.find(event => String(event.id) === String(eventId));
  const names = (match?.hosts || []).map(hostDisplayName).filter(Boolean);
  return names.length > 0 ? names.join(' & ') : null;
}

const ordinal = (n) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
};

// A PinGolf handicap is a stroke adjustment, so its sign carries meaning and a bare "-5" beside
// "Handicap" is ambiguous about which direction it moves you.
// Shown as the strokes applied to a card, not the raw average - see toDisplayHandicap.
const formatHandicap = (value) => formatHandicapText(value);

// What a player wants off this page about themselves: where they stand, what the board is
// scoring them with, and how much of the season they have actually banked.
function buildMyStats(me, standings, season) {
  const countedWeeks = Math.max(season.week_count - season.drop_count, 0);
  const weeksPlayed = (me.weeklyPoints || []).filter(points => points > 0).length;
  const leader = standings[0];
  const pointsBack = leader && leader.seasonTotal > me.seasonTotal
    ? leader.seasonTotal - me.seasonTotal
    : 0;

  return [
    {
      key: 'position',
      label: 'Position',
      value: ordinal(me.rank),
      icon: 'trophy',
      tone: 'warning',
      detailTitle: 'Your Position',
      detailBody: `You are ${ordinal(me.rank)} of ${standings.length} in the season standings.${
        pointsBack > 0
          ? ` The leader is ${pointsBack} point${pointsBack === 1 ? '' : 's'} ahead of you.`
          : ' You are leading the season.'
      }${me.provisional ? ' A week still in progress is included, so this can still move before the week is finalized.' : ''}`
    },
    {
      key: 'handicap',
      label: 'Handicap',
      value: formatHandicap(me.playingHandicap),
      icon: 'target',
      tone: 'brand',
      detailTitle: 'Your Playing Handicap',
      detailBody: `Your playing handicap is ${formatHandicap(me.playingHandicap)}. It is applied to your raw card to get the net score the weekly board ranks on, and it is set from your previous rounds only - the card you are shooting now never changes the handicap applied to it.${
        me.handicapRounds > 0
          ? ` It is built from ${me.handicapRounds} recorded round${me.handicapRounds === 1 ? '' : 's'} so far, so it keeps settling as you play more.`
          : ' You have no recorded rounds yet, so this is still your starting figure.'
      }`
    },
    {
      key: 'points',
      label: 'Points',
      value: me.seasonTotal,
      icon: 'sparkles',
      tone: 'success',
      detailTitle: 'Your Season Points',
      detailBody: `Your season total is ${me.seasonTotal} points - the sum of your best ${countedWeeks} weeks. Each week you play awards points by where you finish on that week's net board, and your ${season.drop_count} worst weeks are dropped from this total.`
    },
    {
      key: 'weeks-played',
      label: 'Weeks Played',
      value: `${weeksPlayed}/${season.week_count}`,
      icon: 'calendar',
      tone: 'info',
      detailTitle: 'Weeks Played',
      detailBody: `You have scored in ${weeksPlayed} of the season's ${season.week_count} weeks.${
        weeksPlayed >= countedWeeks
          ? ` That is already enough to fill all ${countedWeeks} counting weeks, so from here every new week can only replace a worse one.`
          : ` ${countedWeeks} weeks count toward your total, so you have ${countedWeeks - weeksPlayed} more to bank before extra weeks start replacing your worst ones.`
      }`
    }
  ];
}

function getSeasonCardsSold(weeks, pingolfEvents) {
  const seasonEventIds = new Set(
    weeks.flatMap(week => week.sessions || []).map(session => session.event_id)
  );
  return pingolfEvents
    .filter(event => seasonEventIds.has(event.id))
    .reduce((sum, event) => sum + (Number(event.cards_sold) || 0), 0);
}

function PinGolfLeagueOverview() {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();

  const [seasons, setSeasons] = useState([]);
  const [standings, setStandings] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [pingolfEvents, setPingolfEvents] = useState([]);
  const [myWeeks, setMyWeeks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped when the server is reachable again after a failed load, so the page
  // fills itself in instead of waiting for the member to reload it.
  const [reloadKey, setReloadKey] = useState(0);
  useOnReconnect(() => setReloadKey(key => key + 1));

  useEffect(() => {
    let cancelled = false;

    const fetchOverview = async () => {
      try {
        // `loading` starts true; a reload keeps what is on screen until it lands.
        const seasonsRes = await apiService.getPingolfSeasons();
        const seasonList = seasonsRes.data || [];
        if (cancelled) return;
        setSeasons(seasonList);

        const season = seasonList.find(s => s.status === 'active') || seasonList[0];
        if (season) {
          const [standingsRes, weeksRes, eventsRes, myWeeksRes] = await Promise.all([
            apiService.getPingolfSeasonStandings(season.id),
            apiService.getPingolfSeasonWeeks(season.id),
            apiService.get('/events/pingolf/summary'),
            // Own history is supplementary to the page, so a failure here costs the My Season
            // table and nothing else - the league board still renders.
            apiService.getPingolfSeasonMyWeeks(season.id).catch(() => null)
          ]);
          if (cancelled) return;
          setStandings(standingsRes.data || []);
          setWeeks(weeksRes.data || []);
          setPingolfEvents(eventsRes.data || []);
          setMyWeeks(myWeeksRes?.data || null);
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PinGolf League overview:', err);
          setError('Failed to load the PinGolf League overview.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOverview();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const season = useMemo(
    () => seasons.find(s => s.status === 'active') || seasons[0] || null,
    [seasons]
  );
  const currentWeek = useMemo(() => pickCurrentWeek(weeks), [weeks]);
  const nextEvent = useMemo(() => pickNextEvent(weeks, pingolfEvents), [weeks, pingolfEvents]);
  const seasonDateRange = useMemo(() => getSeasonDateRange(season), [season]);
  const cardsSold = useMemo(() => getSeasonCardsSold(weeks, pingolfEvents), [weeks, pingolfEvents]);
  // Once the night has started the card is no longer about what's coming, it's the way in to
  // the night you are standing in - say so, or a player scanning for "where do I buy a card"
  // skips right past it.
  const nextEventStarted = nextEvent
    ? new Date(nextEvent.event_date).getTime() <= Date.now()
    : false;

  const nextEventHosts = useMemo(
    () => (nextEvent ? getEventHostNames(nextEvent.event_id, pingolfEvents) : null),
    [nextEvent, pingolfEvents]
  );
  const seasonPrizePool = cardsSold * Number(season?.season_pool_per_card || 0);
  const hasProvisionalStandings = standings.some(entry => entry.provisional);

  // The viewer's own row, if they have one. Standings only contain players who have turned in
  // a card, so a new member legitimately has no entry here.
  const myStanding = standings.find(entry => String(entry.userId) === String(currentUser?.id)) || null;
  const myStats = myStanding ? buildMyStats(myStanding, standings, season) : [];

  // The numbers a player asks about when they land here: how big is the pot, who am I up
  // against, and how much of the season actually counts. Same tiles-with-explainers treatment
  // the event page uses for a night's card price and pot, since these are the season-scale
  // half of the same story.
  const countedWeeks = season ? Math.max(season.week_count - season.drop_count, 0) : 0;
  const perCard = Number(season?.season_pool_per_card || 0);
  const cardPrice = Number(season?.card_price_dollars || 0);
  const seasonStats = season ? [
    {
      key: 'pool',
      label: 'Prize Pool',
      value: formatCurrency(seasonPrizePool),
      icon: 'trophy',
      tone: 'warning',
      detailTitle: 'Season Prize Pool',
      detailBody: `${perCard ? `${formatMoney(perCard)} from every card sold` : 'A cut of every card sold'} goes into this pool, so it keeps growing all season no matter which nights you play. It pays out on the final standings once the last week is scored.${seasonPrizePool > 0 ? ' At the pool as it stands right now, that pays:' : ' No cards sold yet, so there is nothing in it.'}`,
      detailBreakdown: seasonPrizePool > 0
        ? PRIZE_SPLIT_ROWS
          .map(row => ({ ...row, share: Number((season.season_pool_split || {})[row.key] || 0) }))
          .filter(row => row.share > 0)
          .map(row => ({
            key: row.key,
            label: row.label,
            note: `${Math.round(row.share * 100)}%`,
            amount: formatMoney(seasonPrizePool * row.share)
          }))
        : [],
      detailFootnote: seasonPrizePool > 0
        ? 'The pool grows with every card sold for the rest of the season, so these numbers keep climbing until the season is finalized.'
        : null
    },
    {
      key: 'players',
      label: 'Players',
      value: standings.length,
      icon: 'users',
      tone: 'info',
      detailTitle: 'Players',
      detailBody: `Everyone who has turned in at least one card this season - ${standings.length} so far. There is no roster to join: buy a card on any league night and you are in the standings. Joining late still works, since only your best ${countedWeeks} weeks count.`
    },
    {
      key: 'cards',
      label: 'Cards Sold',
      value: cardsSold,
      icon: 'dollar',
      tone: 'success',
      detailTitle: 'Cards Sold',
      detailBody: `Every PinGolf card bought across the season's league nights so far${cardPrice ? `, at ${formatMoney(cardPrice)} each` : ''}. ${perCard ? `${formatMoney(perCard)} of each card goes into the season prize pool` : 'Each card feeds the season prize pool'} - the rest pays out on the night it was played.`
    },
    {
      key: 'counted',
      // "Weeks" alone reads as the season length, which is not what 6/8 means here. The tile
      // label is nowrap + ellipsis, so on a phone too narrow for the full wording it truncates
      // rather than wrapping and dragging every tile in the row taller.
      label: 'Weeks Counted',
      value: `${countedWeeks}/${season.week_count}`,
      icon: 'calendar',
      tone: 'brand',
      detailTitle: 'Weeks Counted',
      detailBody: season.drop_count > 0
        ? `The season runs ${season.week_count} weeks, but only your best ${countedWeeks} count toward the standings. Your ${season.drop_count === 1 ? 'worst week is' : `${season.drop_count} worst weeks are`} dropped automatically, so a rough night - or a night you cannot make - will not sink your season.`
        : `Every one of the season's ${season.week_count} weeks counts toward the standings. No weeks are dropped, so each night you miss is a night your rivals can gain on you.`
    }
  ] : [];

  if (loading) {
    return (
      <div className="pl-overview-page bn-page-padding">
        <DSCard title="Loading league overview" subtitle="Fetching the latest season status and standings." />
        <PinGolfBottomNav />
      </div>
    );
  }

  const status = season ? (STATUS_CONFIG[season.status] || STATUS_CONFIG.active) : null;

  return (
    <div className="pl-overview-page bn-page-padding">
      <div className="pingolf-hero pl-overview-hero">
        <img src={pingolfHero} alt="" className="pingolf-hero-banner" />
        <h1>{season ? season.name : 'PinGolf League'}</h1>
      </div>

      {error && season ? <DSAlert variant="error" appearance="outline">{error}</DSAlert> : null}

      {/* A failed load and a league with no season both leave `season` empty.
          Telling a member "no season yet" because the server was down sent
          them away believing the league did not exist. */}
      {!season && error ? (
        <DSCard
          title="Couldn't load the PinGolf League"
          subtitle="The league couldn't be reached just now. This page will fill in as soon as the connection is back."
          variant="outlined"
          bgVariant="subtle"
          borderVariant="subtle"
          cornerVariant="lg"
        />
      ) : !season ? (
        <DSCard
          title="No season yet"
          subtitle="A PinGolf League season hasn't been set up yet. Check back soon."
          variant="outlined"
          bgVariant="subtle"
          borderVariant="subtle"
          cornerVariant="lg"
        />
      ) : (
        <div className="pl-overview-sections">
          {/* No card around this block: the stat tiles are already cards, and nesting them
              inside another one boxed the whole hero area twice over. */}
          <section className="pl-overview-summary">
            <div className="pl-overview-status-row">
              <p className="pl-overview-season-meta">
                <DSIcon name="calendar" size="sm" tone="muted" />
                {currentWeek ? `Week ${currentWeek.week_number} of ${season.week_count}` : `${season.week_count}-week season`}
                {seasonDateRange ? ` · ${seasonDateRange}` : ''}
              </p>
              <DSBadge variant={status.variant} appearance="outline">{status.label}</DSBadge>
            </div>

            <PinGolfStats stats={seasonStats} ariaLabel="Season stats" />

            <div className="pl-overview-section-footer">
              <DSButton
                appearance="text"
                tone="neutral"
                size="sm"
                trailingIcon={<DSIcon name="arrowRight" size="sm" tone="default" />}
                onClick={() => navigate(`/pingolf-season/${season.id}`)}
              >
                Learn more about the league
              </DSButton>
            </div>
          </section>

          <section className="pl-overview-section">
            <h2>{nextEventStarted ? 'Happening Now' : 'Next Event'}</h2>
            {nextEvent ? (
              <div
                className="pl-overview-next-event-card"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/events/${nextEvent.event_id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/events/${nextEvent.event_id}`);
                  }
                }}
              >
                <DSCard variant="outlined" bgVariant="default" borderVariant="subtle" cornerVariant="lg">
                  <div className="pl-overview-next-event">
                    <span className="pl-overview-next-event-icon" aria-hidden="true">
                      <DSIcon name="calendar" size="md" tone="brand" />
                    </span>
                    <div className="pl-overview-next-event-text">
                      <strong>
                        {nextEventTitle(nextEvent)}
                        {nextEventStarted && (
                          <> <DSBadge variant="success" size="sm">Playing now</DSBadge></>
                        )}
                      </strong>
                      <div className="pl-overview-next-event-meta">
                        {formatEventDate(nextEvent.event_date)} - {formatEventTime(nextEvent.event_date)}
                      </div>
                      {nextEventHosts && (
                        <div className="pl-overview-next-event-host">Hosted by {nextEventHosts}</div>
                      )}
                    </div>
                    <DSIcon name="arrowRight" size="sm" tone="muted" />
                  </div>
                </DSCard>
              </div>
            ) : (
              <DSCard
                title="Nothing scheduled yet"
                subtitle="No upcoming sessions are on the calendar right now."
                variant="outlined"
                bgVariant="subtle"
                borderVariant="subtle"
                cornerVariant="lg"
              />
            )}

            {/* The events list is the season schedule: every league night grouped into active,
                upcoming and past. It carries its own bottom nav, so a member who has no Events
                tab can still get back to League from there. */}
            <div className="pl-overview-section-footer">
              <DSButton
                appearance="text"
                tone="neutral"
                size="sm"
                trailingIcon={<DSIcon name="arrowRight" size="sm" tone="default" />}
                onClick={() => navigate(EVENTS_PATH)}
              >
                See all events
              </DSButton>
            </div>
          </section>

          {/* The league board first: Leaderboard now means exactly that, everyone's standings,
              with nothing of the viewer's own mixed into it. */}
          <section className="pl-overview-section">
            <div className="pl-overview-section-heading">
              <h2>Leaderboard</h2>
              {hasProvisionalStandings && <DSBadge variant="success" size="sm" uppercase>Live</DSBadge>}
            </div>

            {standings.length === 0 ? (
              <DSCard
                title="No standings yet"
                subtitle="Standings appear as soon as the first cards are turned in."
                variant="outlined"
                bgVariant="subtle"
                borderVariant="subtle"
                cornerVariant="lg"
              />
            ) : (
              // The card gives up its padding so the board can use the full width - the table
              // re-applies the inset on its outer columns only, which keeps the text off the
              // edge while the scroll region runs edge to edge.
              <div className="pl-overview-standings-card">
                <DSCard variant="outlined" bgVariant="default" borderVariant="subtle" cornerVariant="lg">
                  <PinGolfStandingsTable
                    standings={standings}
                    weekCount={season.week_count}
                    dropCount={season.drop_count}
                    flush
                  />
                </DSCard>
              </div>
            )}
          </section>

          {/* Then the viewer's own season: where they stand, and the week-by-week history that
              makes their handicap explainable. Before anyone has turned in a card there is no
              "my season" to have, and the Leaderboard's empty state above says so already. */}
          {standings.length > 0 && (
            <section className="pl-overview-section">
              <div className="pl-overview-section-heading">
                <h2>My Season</h2>
                {myWeeks?.weeks?.some(week => week.provisional) && <DSBadge variant="success" size="sm" uppercase>Live</DSBadge>}
              </div>

              {myStats.length > 0 ? (
                <>
                  <PinGolfStats stats={myStats} ariaLabel="Your season stats" />

                  {myWeeks?.weeks?.length > 0 && (
                    <div className="pl-overview-my-weeks">
                      <h3>Week by week</h3>
                      <PinGolfMyWeeks
                        weeks={myWeeks.weeks}
                        weekCount={myWeeks.week_count}
                        dropCount={myWeeks.drop_count}
                      />
                    </div>
                  )}
                </>
              ) : (
                <DSCard
                  title="You're not in the standings yet"
                  subtitle="Buy a card on any league night and you'll show up here from your first turned-in card."
                  variant="outlined"
                  bgVariant="subtle"
                  borderVariant="subtle"
                  cornerVariant="lg"
                />
              )}
            </section>
          )}
        </div>
      )}

      <PinGolfBottomNav />
    </div>
  );
}

export default PinGolfLeagueOverview;
